import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, Send, AlertTriangle, CheckCircle2, Info, Clock, Copy, UserX, Pencil, Check, X, Calendar } from 'lucide-react';
import client from '../../api/client';
import DetalleNoDisponible, { motivoDeCarga } from '../../components/shared/DetalleNoDisponible';
import { formatHours } from '../../utils/formatHours';
import { useAuth, userIsAdmin, userIsEngineer, userIsManager, userIsReviewer } from '../../context/AuthContext';
import SearchableSelect from '../../shared/components/SearchableSelect';
import { OtrosProyectosChips, OtrosProyectosDetalle } from '../components/OtrosProyectos';
import { empleadosQueSeDescartan, avisoDescartados } from '../../utils/empleadosDescartados';

/**
 * V67 — Tareas que exigen la nota de dificultad.
 * ⚠ ANTI-DIVERGENCIA: espeja `TAREAS_CON_NOTA_OBLIGATORIA` de nominaController. Se compara
 * por CÓDIGO y no por id porque los ids difieren entre local y prod; si acá y allá
 * divergen, la pantalla pide una nota que el backend no exige (o peor, al revés: el
 * usuario guarda y recibe un error sin ver el campo).
 */
const TAREAS_CON_NOTA = ['01080'];
import { NAVY, AMBER, AMBER_DARK, BORDER_COLOR, bubbleStyle, navyBtn, fieldStyle } from '../../styles/tokens';
import { formatGlLabel } from '../../utils/formatGl';
import { ABSENCE_REASONS } from '@shared/constants';

import { etiquetaProyectoTitulo } from '../../utils/formatProject';
const STATUS_LABELS = { draft: 'Borrador', submitted: 'Sometido', approved: 'Aprobado', returned: 'Devuelto' };
const STATUS_COLORS = {
  draft: { bg: '#f3e8ff', color: '#7c3aed' },
  submitted: { bg: '#fef9c3', color: '#ca8a04' },
  approved: { bg: '#dcfce7', color: '#16a34a' },
  returned: { bg: '#fed7aa', color: '#ea580c' },
};

function fmtDate(d) {
  if (!d) return '';
  const [y, m, day] = String(d).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function TarjetaPage() {
  const { sheetId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [sheet, setSheet] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  const [projects, setProjects] = useState([]);

  // Employee cards: { empId, empNotes, entries: [{ costCodeId, regular, overtime, notes, difficultyNote, entryId? }], alerts: {} }
  const [cards, setCards] = useState([]);
  const [sheetNotes, setSheetNotes] = useState('');

  // Unpaid absences (Ausencias no remuneradas): { id, employee_id, employee_name, reason, hours, notes }
  const [absences, setAbsences] = useState([]);
  const [addingAbsence, setAddingAbsence] = useState(false);
  const [pendingAbs, setPendingAbs] = useState({ employee_id: null, reason: 'class', notes: '' });
  const [convertingCard, setConvertingCard] = useState(null); // empId
  const [convertReason, setConvertReason] = useState('class');
  const [editingAbsenceId, setEditingAbsenceId] = useState(null);
  const [editingAbsData, setEditingAbsData] = useState({ reason: 'class', notes: '' });

  const [loading, setLoading] = useState(true);
  const [errorCarga, setErrorCarga] = useState(null);  // B-1
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [copyingFrom, setCopyingFrom] = useState(null); // empId being copied from
  const [showExitModal, setShowExitModal] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [dateInfo, setDateInfo] = useState(null); // { is_holiday, holiday_name, is_weekend, day_of_week }

  // AMGtime validation
  const [amgValidation, setAmgValidation] = useState(null);
  const [amgLoading, setAmgLoading] = useState(false);
  const [amgError, setAmgError] = useState(null);
  const canSeeAmg = userIsAdmin(user) || userIsEngineer(user) || userIsManager(user) || userIsReviewer(user);

  // Create mode: project_id and date from URL params
  const projectIdParam = searchParams.get('project');
  const dateParam = searchParams.get('date');

  // Check if date is holiday or weekend
  async function updateDateInfo(dateStr) {
    if (!dateStr) { setDateInfo(null); return; }
    const d = new Date(dateStr + 'T12:00:00');
    const dow = d.getDay();
    const isWeekend = dow === 0 || dow === 6;
    try {
      const year = dateStr.slice(0, 4);
      const res = await client.get(`/api/admin/holidays?year=${year}`);
      const hol = (res.data || []).find(h => String(h.date).slice(0, 10) === dateStr && h.is_active !== false);
      setDateInfo({ is_weekend: isWeekend, is_holiday: !!hol, holiday_name: hol?.name || null, day_of_week: dow });
    } catch {
      setDateInfo({ is_weekend: isWeekend, is_holiday: false, holiday_name: null, day_of_week: dow });
    }
  }

  // Fetch reference data + sheet
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const projRes = await client.get('/api/projects');
      setProjects(projRes.data.filter(p => p.active !== false));

      let sheetData = null;
      if (sheetId && sheetId !== 'new') {
        // Load existing sheet
        const res = await client.get(`/api/nomina/sheets/${sheetId}`);
        sheetData = res.data;
        setSheet(res.data);
        setSheetNotes(res.data.notes || '');
      } else if (projectIdParam && dateParam) {
        // Create new sheet
        const res = await client.post('/api/nomina/sheets', {
          project_id: parseInt(projectIdParam),
          sheet_date: dateParam,
        });
        sheetData = res.data;
        setSheet(res.data);
        setSheetNotes('');
        setCards([]);
      }

      // Fetch employees + cost codes filtered by project
      const projectId = sheetData?.project_id || projectIdParam;
      const empParams = projectId ? { project_id: projectId } : {};
      const ccParams = projectId ? { project_id: projectId } : {};
      const [empRes, ccRes, glRes] = await Promise.all([
        client.get('/api/nomina/employees', { params: empParams }),
        client.get('/api/nomina/cost-codes', { params: ccParams }),
        client.get('/api/nomina/gl-accounts'),
      ]);
      setEmployees(empRes.data);
      setCostCodes(ccRes.data);
      setGlAccounts(glRes.data);

      if (sheetData?.entries) {
        buildCardsFromEntries(sheetData.entries, empRes.data, sheetData);
      }
      setAbsences(sheetData?.absences || []);

      // Check date info (holiday/weekend) — global, not per employee
      if (sheetData) {
        updateDateInfo(String(sheetData.sheet_date).slice(0, 10));
      }

      // Fetch AMGtime punch data once on load (badges do live comparison client-side)
      const canSeeAmg = userIsAdmin(user) || userIsEngineer(user) || userIsManager(user) || userIsReviewer(user);
      if (sheetData?.id && canSeeAmg) {
        setAmgLoading(true);
        client.get(`/api/nomina/sheets/${sheetData.id}/amgtime-validation`)
          .then(res => setAmgValidation(res.data))
          .catch(err => setAmgError(err.response?.data?.error || 'Error al consultar AMGtime'))
          .finally(() => setAmgLoading(false));
      }
    } catch (err) {
      console.error(err);
      if (err.response?.status === 409) {
        alert(err.response.data.error);
      } else if (sheetId && sheetId !== 'new') {
        // ⚠ Solo cuando se pidio una tarjeta POR ID. Esta pantalla tambien CREA
        // (`projectIdParam` + `dateParam`), y ahi "no encontrada" no es el caso.
        setErrorCarga(motivoDeCarga(err));
      }
    } finally {
      setLoading(false);
    }
  }, [sheetId, projectIdParam, dateParam]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // V32.8: per-project nómina coding mode. Resolved from the project (works for new
  // sheets too, since getSheet only carries it once saved) with a 'tareas' fallback.
  const sheetProjectId = sheet?.project_id || (projectIdParam ? parseInt(projectIdParam, 10) : null);
  const codingMode = projects.find(p => p.id === sheetProjectId)?.nomina_coding_mode
    || sheet?.nomina_coding_mode || 'tareas';
  const isGlMode = codingMode === 'gl_accounts';

  // Build card structure from entries
  function buildCardsFromEntries(entries, empList, sheetData) {
    const byEmp = {};
    entries.forEach(e => {
      if (!byEmp[e.employee_id]) {
        byEmp[e.employee_id] = {
          empId: e.employee_id,
          empName: e.employee_name,
          empNotes: e.notes || '',
          workLocation: e.work_location || 'in-person',
          entries: [],
          alerts: null,
        };
      }
      // Use first non-empty notes as empNotes
      if (!byEmp[e.employee_id].empNotes && e.notes) {
        byEmp[e.employee_id].empNotes = e.notes;
      }
      // Use first non-default work_location
      if (e.work_location && e.work_location !== 'in-person') {
        byEmp[e.employee_id].workLocation = e.work_location;
      }
      byEmp[e.employee_id].entries.push({
        entryId: e.id,
        costCodeId: e.cost_code_id,
        glCodeId: e.gl_code_id,
        regular: parseFloat(e.regular_hours) || 0,
        overtime: parseFloat(e.overtime_hours) || 0,
        notes: e.notes || '',
        difficultyNote: e.difficulty_note || '',
      });
    });
    const cardList = Object.values(byEmp);
    setCards(cardList);

    // Fetch alerts for all employees in one batch request (avoids per-IP rate
    // limiting that fired when gangas with many workers loaded N parallel GETs).
    const empIds = cardList.map(c => c.empId);
    if (empIds.length > 0) {
      client.post('/api/nomina/hours-check/batch', {
        employeeIds: empIds,
        date: String(sheetData.sheet_date).slice(0, 10),
        excludeSheetId: sheetData.id,
      })
        .then(res => {
          const byId = res.data || {};
          setCards(prev => prev.map(c => ({ ...c, alerts: byId[c.empId] || null })));
        })
        .catch(() => {});
    }
  }

  // Fetch cross-project hours for an employee (Changes 3, 5)
  async function fetchAlerts(employeeId, date, excludeSheetId) {
    try {
      const res = await client.get(`/api/nomina/hours-check/${employeeId}/${date}`, {
        params: { excludeSheetId },
      });
      return res.data;
    } catch {
      return null;
    }
  }

  // Find AMGtime data for a specific employee (by name match against validation data)
  function getAmgForEmployee(empName) {
    if (!amgValidation?.validated || !empName) return null;
    const matched = amgValidation.matched?.find(m => m.name === empName);
    if (matched) return { status: 'match' };
    const disc = amgValidation.discrepancies?.find(d => d.name === empName);
    if (disc) return { status: disc.type, ...disc };
    return null; // employee not in AMGtime data (no external_id)
  }

  // Takeover: engineer/manager on the ganga OR the project's Nómina reviewer
  // (V26.1) can edit a supervisor's draft / returned-to-supervisor sheet when
  // the supervisor is out.
  const isGangaEngineer = sheet?.is_current_user_ganga_engineer && user.roles?.includes('engineer');
  const isGangaManager  = sheet?.is_current_user_ganga_manager  && user.roles?.includes('manager');
  const isProjectReviewer = sheet?.is_current_user_nomina_reviewer && user.roles?.includes('reviewer');
  const isTakeoverLead  = isGangaEngineer || isGangaManager || isProjectReviewer;
  const isEditable = sheet && (
    user.roles?.includes('admin') ||
    (sheet.status === 'draft' && (sheet.supervisor_id === user.id || isTakeoverLead)) ||
    (sheet.status === 'returned' && (
      (sheet.current_reviewer_role === 'supervisor' && (sheet.supervisor_id === user.id || isTakeoverLead)) ||
      (sheet.current_reviewer_role === 'engineer' && user.roles?.includes('engineer')) ||
      (sheet.current_reviewer_role === 'manager' && user.roles?.includes('manager'))
    ))
  );

  // Show a takeover badge when the backend recorded a real takeover and we
  // have the target (natural approver) name to attribute to.
  const showTakeoverBadge = sheet?.last_edited_by_user_id
    && sheet.last_edited_by_user_id !== sheet.supervisor_id
    && sheet?.last_editor_name
    && sheet?.takeover_target_name;

  // ── Card management ──────────────────────────────────────────────────────

  // Fetch AMGtime punch data (GET from server — fetches raw punch data for comparison)
  // Badges do live comparison client-side using punch data, so no need to re-fetch on card changes
  function refreshAmgValidation() {
    if (!sheet?.id || !canSeeAmg) return;
    setAmgLoading(true); setAmgError(null);
    client.get(`/api/nomina/sheets/${sheet.id}/amgtime-validation`)
      .then(res => setAmgValidation(res.data))
      .catch(err => setAmgError(err.response?.data?.error || 'Error'))
      .finally(() => setAmgLoading(false));
  }

  function addEmployee(empId) {
    if (!empId || cards.find(c => c.empId === empId)) return;
    const emp = employees.find(e => e.id === empId);
    const newCard = {
      empId,
      empName: emp?.name || 'Empleado',
      empNotes: '',
      workLocation: 'in-person',
      entries: [{ costCodeId: null, glCodeId: null, regular: 0, overtime: 0, notes: '', difficultyNote: '' }],
      alerts: null,
    };
    setCards([...cards, newCard]);

    // Fetch alerts
    if (sheet) {
      fetchAlerts(empId, String(sheet.sheet_date).slice(0, 10), sheet.id).then(alerts => {
        setCards(prev => prev.map(c => c.empId === empId ? { ...c, alerts } : c));
      });
    }
  }

  function removeEmployee(empId) {
    setCards(cards.filter(c => c.empId !== empId));
  }

  function addTask(empId) {
    setCards(cards.map(c => {
      if (c.empId !== empId) return c;
      return { ...c, entries: [...c.entries, { costCodeId: null, glCodeId: null, regular: 0, overtime: 0, notes: '', difficultyNote: '' }] };
    }));
  }

  function removeTask(empId, taskIdx) {
    setCards(cards.map(c => {
      if (c.empId !== empId) return c;
      return { ...c, entries: c.entries.filter((_, i) => i !== taskIdx) };
    }));
  }

  function copyActivitiesToEmployee(sourceEmpId, targetEmpId) {
    if (!targetEmpId || cards.find(c => c.empId === targetEmpId)) return;
    const sourceCard = cards.find(c => c.empId === sourceEmpId);
    if (!sourceCard) return;
    const emp = employees.find(e => e.id === targetEmpId);
    const newCard = {
      empId: targetEmpId,
      empName: emp?.name || 'Empleado',
      empNotes: '',
      entries: sourceCard.entries.map(e => ({
        costCodeId: e.costCodeId,
        regular: e.regular,
        overtime: e.overtime,
        notes: '',
        // V67: la nota viaja con la tarea clonada. Clonar la cuadrilla y perder la nota
        // dejaría la tarjeta nueva inválida sin decir por qué.
        difficultyNote: e.difficultyNote || '',
      })),
      alerts: null,
    };
    setCards(prev => [...prev, newCard]);

    if (sheet) {
      fetchAlerts(targetEmpId, String(sheet.sheet_date).slice(0, 10), sheet.id).then(alerts => {
        setCards(prev => prev.map(c => c.empId === targetEmpId ? { ...c, alerts } : c));
      });
    }
  }

  function updateTask(empId, taskIdx, field, value) {
    if (submitError) setSubmitError(null); // clear submit error when user edits
    setCards(cards.map(c => {
      if (c.empId !== empId) return c;
      const newEntries = [...c.entries];
      newEntries[taskIdx] = { ...newEntries[taskIdx], [field]: value };
      return { ...c, entries: newEntries };
    }));
  }

  function updateEmpNotes(empId, value) {
    setCards(cards.map(c => c.empId === empId ? { ...c, empNotes: value } : c));
  }

  // ── V63 · Aviso de ausencia de PTO ────────────────────────────────────────
  // ⚠⚠ UNA SOLA EXPRESIÓN PARA LAS DOS SUPERFICIES. El resumen de arriba y la marca de la
  // fila salen de `absenceFor`, y su apariencia de `describeAbsence`. Si cada una calculara
  // lo suyo podrían discrepar sobre la MISMA persona — y el lector no tendría forma de
  // saber cuál creer.

  /**
   * La ausencia a mostrar para un empleado, o `null`.
   * Prioriza APROBADA sobre pendiente: si tiene las dos, la aprobada es la accionable.
   * ⚠ Si la persona ya está en "Ausencias no remuneradas" NO se avisa: ya fue tratada, y
   * una ausencia no remunerada justamente NO lleva código fringe — avisar ahí sería
   * empujar a lo contrario de lo que corresponde.
   */
  function absenceFor(empId) {
    if (absences.some(a => a.employee_id === empId)) return null;
    const card = cards.find(c => c.empId === empId);
    const lista = card?.alerts?.pto_absences || [];
    return lista.find(a => a.status === 'approved') || lista[0] || null;
  }

  const cardsConAusencia = cards
    .map(c => ({ card: c, ausencia: absenceFor(c.empId) }))
    .filter(x => x.ausencia);

  /**
   * Texto y color SALEN DEL MISMO OBJETO. Separados se arregla uno y se olvida el otro —
   * pasó en V62, donde una solicitud denegada salía en verde con el texto correcto.
   */
  function describeAbsence(a) {
    const tipo = a.type === 'sick' ? 'Enfermedad' : 'Vacaciones';
    const horas = a.hours_per_day ? ` · ${formatHours(a.hours_per_day)} h` : '';
    return a.status === 'approved'
      ? { texto: `${tipo} aprobada${horas}`, color: '#16a34a', bg: '#f0fdf4', borde: '#16a34a40', accionable: true }
      // Pendiente NO habilita cargar el fringe: nadie la autorizó todavía.
      : { texto: `${tipo} — solicitud pendiente`, color: '#92400e', bg: '#fef3c7', borde: '#fde68a', accionable: false };
  }

  /** ¿La fila ya tiene cargada la tarea fringe de este target? */
  function tieneTareaFringe(card, target) {
    if (!target) return false;
    return (card?.entries || []).some(e => target.mode === 'gl_accounts'
      ? Number(e.glCodeId) === Number(target.gl_code_id)
      : Number(e.costCodeId) === Number(target.cost_code_id));
  }

  /** Una fila sin código y sin horas — la que deja `addEmployee` al crear la tarjeta. */
  const filaVacia = (e) => !e.costCodeId && !e.glCodeId
    && !(parseFloat(e.regular) || 0) && !(parseFloat(e.overtime) || 0);

  /**
   * Carga la tarea/GL del código fringe con las horas del día ya puestas.
   *
   * ⚠ REUTILIZA LA FILA VACÍA si la hay, en vez de agregar una. `addEmployee` crea la
   * tarjeta con una fila en blanco, así que appendear dejaba la tarea cargada Y una fila
   * vacía debajo que había que borrar a mano — el botón creaba trabajo en lugar de
   * ahorrarlo. Solo agrega cuando no hay ninguna vacía que ocupar.
   */
  function cargarAusencia(empId, ausencia, target) {
    const fringe = {
      costCodeId: target.mode === 'tareas' ? target.cost_code_id : null,
      glCodeId:   target.mode === 'gl_accounts' ? target.gl_code_id : null,
      regular: ausencia.hours_per_day || 0,
      overtime: 0,
      notes: '',
    };
    setCards(cards.map(c => {
      if (c.empId !== empId) return c;
      const idx = c.entries.findIndex(filaVacia);
      const entries = idx >= 0
        ? c.entries.map((e, i) => (i === idx ? { ...e, ...fringe } : e))
        : [...c.entries, fringe];
      return { ...c, entries };
    }));
  }

  // ── Unpaid absences (Ausencias no remuneradas) ───────────────────────────

  async function saveNewAbsence() {
    if (!pendingAbs.employee_id || !pendingAbs.reason || !sheet?.id) return;
    try {
      const res = await client.post(`/api/nomina/sheets/${sheet.id}/absences`, {
        employee_id: pendingAbs.employee_id,
        reason: pendingAbs.reason,
        notes: pendingAbs.notes || null,
      });
      // Resolve employee_name from local employees list
      const emp = employees.find(e => e.id === pendingAbs.employee_id);
      setAbsences(prev => [...prev, { ...res.data, employee_name: emp?.name || '' }]);
      setAddingAbsence(false);
      setPendingAbs({ employee_id: null, reason: 'class', notes: '' });
    } catch (err) {
      alert(err.response?.data?.error || 'Error al crear ausencia.');
    }
  }

  async function removeAbsence(absenceId) {
    try {
      await client.delete(`/api/nomina/absences/${absenceId}`);
      setAbsences(prev => prev.filter(a => a.id !== absenceId));
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar ausencia.');
    }
  }

  function startEditAbsence(a) {
    setEditingAbsenceId(a.id);
    setEditingAbsData({ reason: a.reason, notes: a.notes || '' });
  }

  async function saveAbsenceEdit() {
    if (!editingAbsenceId) return;
    try {
      const res = await client.patch(`/api/nomina/absences/${editingAbsenceId}`, {
        reason: editingAbsData.reason,
        notes: editingAbsData.notes || null,
      });
      setAbsences(prev => prev.map(a =>
        a.id === editingAbsenceId ? { ...a, ...res.data } : a
      ));
      setEditingAbsenceId(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al actualizar ausencia.');
    }
  }

  async function convertCardToAbsence(empId, reason) {
    if (!sheet?.id || !empId || !reason) return;
    try {
      const res = await client.post(`/api/nomina/sheets/${sheet.id}/convert-to-absence`, {
        employee_id: empId,
        reason,
      });
      const emp = employees.find(e => e.id === empId);
      const card = cards.find(c => c.empId === empId);
      setCards(prev => prev.filter(c => c.empId !== empId));
      setAbsences(prev => {
        // If this employee already had an absence row for this reason, replace it
        const without = prev.filter(a => !(a.employee_id === empId && a.reason === reason));
        return [...without, { ...res.data, employee_name: card?.empName || emp?.name || '' }];
      });
      setConvertingCard(null);
      setConvertReason('class');
    } catch (err) {
      alert(err.response?.data?.error || 'Error al convertir a ausencia.');
    }
  }

  // ── Compute max regular for employee ─────────────────────────────────────

  function getMaxRegular(card) {
    if (!card.alerts) return 8;
    const dailyRemaining = Math.max(0, 8 - (card.alerts.total_regular || 0));
    const weeklyRemaining = card.alerts.weekly_remaining ?? 40;
    return Math.min(dailyRemaining, weeklyRemaining);
  }

  function getCapStatus(card) {
    if (!card.alerts) return 'unknown';
    const otherRegular = card.alerts.total_regular || 0;
    const thisSheetRegular = card.entries.reduce((s, e) => s + (parseFloat(e.regular) || 0), 0);
    const dailyTotal = otherRegular + thisSheetRegular;
    const weeklyRemaining = card.alerts.weekly_remaining ?? 40;
    // Check weekly cap first (stricter when accumulated)
    if (weeklyRemaining <= 0) return 'exceeded';
    if (dailyTotal > 8) return 'exceeded';
    if (dailyTotal === 8 || weeklyRemaining - thisSheetRegular <= 0) return 'reached';
    if (otherRegular > 0 || (card.alerts.weekly_regular || 0) > 0) return 'partial';
    return 'ok';
  }

  // ── Save & Submit ─────────────────────────────────────────────────────────

  function buildEntries() {
    const entries = [];
    cards.forEach(card => {
      card.entries.forEach(e => {
        // GL-mode projects key entries on the GL account; tareas projects on the cost code.
        const codeId = isGlMode ? e.glCodeId : e.costCodeId;
        if (codeId) {
          entries.push({
            employee_id: card.empId,
            cost_code_id: isGlMode ? null : e.costCodeId,
            gl_code_id: isGlMode ? e.glCodeId : null,
            regular_hours: parseFloat(e.regular) || 0,
            overtime_hours: parseFloat(e.overtime) || 0,
            notes: card.empNotes || null,
            work_location: card.workLocation || 'in-person',
            // ⚠ Se manda SIEMPRE lo que haya en el estado, aunque la tarea ya no pida
            // nota. Si alguien escribe la nota y después cambia de tarea, el input
            // desaparece pero el texto NO se borra: al volver a elegir 01080 lo recupera.
            // Descartarlo al vuelo sería una pérdida silenciosa de algo que el usuario
            // escribió.
            difficulty_note: e.difficultyNote || null,
          });
        }
      });
    });
    return entries;
  }

  // VNA-50 — `buildEntries` descarta las filas sin tarea; si eso deja a un empleado afuera
  // (o tira horas), se frena acá con su nombre en vez de decir "✓ Guardado" y perderlo.
  function frenarSiSeDescartan() {
    const nombres = empleadosQueSeDescartan(cards.map(card => ({
      nombre: card.empName,
      filas: card.entries.map(e => ({
        seGuarda: Boolean(isGlMode ? e.glCodeId : e.costCodeId),
        horas: (parseFloat(e.regular) || 0) + (parseFloat(e.overtime) || 0),
      })),
    })));
    if (nombres.length === 0) return false;
    alert(avisoDescartados(nombres));
    return true;
  }

  async function handleSave() {
    if (!sheet) return;
    if (frenarSiSeDescartan()) return;
    setSaving(true);
    setSubmitError(null);
    try {
      const entriesToSave = buildEntries();
      const res = await client.put(`/api/nomina/sheets/${sheet.id}/entries`, {
        entries: entriesToSave,
        notes: sheetNotes,
      });
      setSheet(res.data);
      // Don't rebuild cards from server — local state is source of truth during editing
      setSaveMsg('✓ Guardado');
      setTimeout(() => setSaveMsg(''), 2000);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al guardar.');
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmit() {
    if (!sheet) return;
    if (frenarSiSeDescartan()) return;
    const entriesToSave = buildEntries();
    if (entriesToSave.length === 0) {
      alert('Agrega al menos un empleado con tarea y horas antes de someter.');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Save first
      await client.put(`/api/nomina/sheets/${sheet.id}/entries`, {
        entries: entriesToSave,
        notes: sheetNotes,
      });
      // Then submit
      const res = await client.post(`/api/nomina/sheets/${sheet.id}/submit`);
      setSheet(res.data);
      // Redirect to the project's nómina sheet list (same destination as this page's
      // Volver/exit nav; reachable by every role that can open the tarjeta). Landing
      // there with the card now in its submitted status is the success confirmation.
      navigate(`/horas/proyecto/${sheet.project_id}`);
    } catch (err) {
      if (err.response?.status === 422 && err.response.data.error === 'REGULAR_CAP_EXCEEDED') {
        setSubmitError(err.response.data);
      } else {
        alert(err.response?.data?.error || 'Error al enviar.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Cargando...</div>;
  // B-1: el motivo lo pone el backend — distingue "no existe" de "no autorizado".
  if (errorCarga) return <DetalleNoDisponible motivo={errorCarga} onVolver={() => navigate('/horas')} volverLabel="Volver a Nómina" />;
  if (!sheet) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Tarjeta no encontrada.</div>;

  const project = projects.find(p => p.id === sheet.project_id);
  const statusCfg = STATUS_COLORS[sheet.status] || STATUS_COLORS.draft;
  const availableEmployees = employees.filter(e =>
    !cards.find(c => c.empId === e.id) &&
    !absences.find(a => a.employee_id === e.id)
  );

  return (
    <div className="page-pad" style={{ padding: '20px 24px', maxWidth: 800, margin: '0 auto' }}>
      {/* Back */}
      <button onClick={() => {
        if (isEditable) { setShowExitModal(true); } else { navigate(`/horas/proyecto/${sheet?.project_id}`); }
      }} style={{
        display: 'flex', alignItems: 'center', gap: 6,
        background: 'none', border: 'none', cursor: 'pointer',
        color: '#64748b', fontSize: 13, marginBottom: 14, fontFamily: 'Inter, sans-serif',
      }}>
        <ArrowLeft size={16} /> Volver
      </button>

      {/* Header */}
      <div style={{ ...bubbleStyle, padding: '16px 20px', marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <h1 style={{ fontSize: 16, fontWeight: 700, color: NAVY, margin: '0 0 3px' }}>
              {etiquetaProyectoTitulo(project?.code, project?.name, { sinNombre: 'Proyecto' })}
            </h1>
            {isEditable ? (
              <input
                type="date"
                value={String(sheet.sheet_date).slice(0, 10)}
                max={new Date().toISOString().slice(0, 10)}
                onChange={async (e) => {
                  const newDateVal = e.target.value;
                  if (!newDateVal) return;
                  try {
                    const res = await client.put(`/api/nomina/sheets/${sheet.id}`, { sheet_date: newDateVal });
                    setSheet(res.data);
                    updateDateInfo(newDateVal);
                    // Re-fetch alerts for all employees in ONE batch request (mirrors
                    // the initial-load batch above). Avoids an N-per-card GET burst that
                    // tripped the per-minute rate limiter on large gangas (R1).
                    const empIds = cards.map(c => c.empId);
                    if (empIds.length > 0) {
                      client.post('/api/nomina/hours-check/batch', {
                        employeeIds: empIds,
                        date: newDateVal,
                        excludeSheetId: sheet.id,
                      })
                        .then(res2 => {
                          const byId = res2.data || {};
                          setCards(prev => prev.map(c => ({ ...c, alerts: byId[c.empId] || null })));
                        })
                        .catch(() => {});
                    }
                    // Re-fetch AMGtime validation for new date (uses live card data)
                    refreshAmgValidation();
                  } catch (err) {
                    alert(err.response?.data?.error || 'Error al cambiar fecha.');
                  }
                }}
                style={{ ...fieldStyle, fontSize: 13, padding: '3px 8px', width: 'auto' }}
              />
            ) : (
              <div style={{ fontSize: 13, color: '#64748b' }}>{fmtDate(sheet.sheet_date)}</div>
            )}
            {sheet.supervisor_name && (
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Creado por: {sheet.supervisor_name}</div>
            )}
            {showTakeoverBadge && (
              <div style={{
                display: 'inline-block', marginTop: 4, padding: '2px 8px',
                background: '#fef3c7', color: '#92400e',
                border: '1px solid #fde68a', borderRadius: 10,
                fontSize: 11, fontWeight: 600,
              }}>
                Editado por {sheet.last_editor_name} en nombre de {sheet.takeover_target_name}
              </div>
            )}
          </div>
          <span style={{
            padding: '3px 12px', borderRadius: 12,
            background: statusCfg.bg, color: statusCfg.color,
            fontSize: 11, fontWeight: 600,
          }}>
            {STATUS_LABELS[sheet.status]}
          </span>
        </div>
      </div>

      {/* Holiday / Weekend global banner */}
      {dateInfo?.is_holiday && (
        <div style={{
          ...bubbleStyle, padding: '10px 16px', marginBottom: 14,
          borderLeft: '3px solid #7c3aed', background: '#f5f3ff',
          fontSize: 13, color: '#5b21b6', fontWeight: 500,
        }}>
          🏖 Este día es feriado: <strong>{dateInfo.holiday_name}</strong>
        </div>
      )}
      {dateInfo?.is_weekend && !dateInfo?.is_holiday && (
        <div style={{
          ...bubbleStyle, padding: '10px 16px', marginBottom: 14,
          borderLeft: '3px solid #64748b', background: '#f8fafc',
          fontSize: 13, color: '#475569', fontWeight: 500,
        }}>
          📅 Este día es fin de semana ({dateInfo.day_of_week === 6 ? 'sábado' : 'domingo'})
        </div>
      )}

      {/* Return reason (non-editable, read-only) */}
      {sheet.status === 'returned' && sheet.return_reason && (
        <div style={{
          ...bubbleStyle, padding: '12px 16px', marginBottom: 14,
          borderLeft: '3px solid #ea580c', background: '#fff7ed',
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#ea580c', marginBottom: 3 }}>Razón de devolución:</div>
          <div style={{ fontSize: 13, color: '#9a3412' }}>{sheet.return_reason}</div>
        </div>
      )}

      {/* Reviewer notes */}
      {sheet.status === 'returned' && sheet.reviewer_notes && sheet.reviewer_notes !== sheet.return_reason && (
        <div style={{
          ...bubbleStyle, padding: '12px 16px', marginBottom: 14,
          borderLeft: '3px solid #f59e0b', background: '#fffbeb',
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#92400e', marginBottom: 3 }}>Notas del revisor:</div>
          <div style={{ fontSize: 13, color: '#78350f' }}>{sheet.reviewer_notes}</div>
        </div>
      )}

      {/* Submit error (Change 5) */}
      {submitError && submitError.violations && (
        <div style={{
          ...bubbleStyle, padding: '14px 16px', marginBottom: 14,
          borderLeft: '3px solid #ef4444', background: '#fef2f2',
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#ef4444', marginBottom: 6 }}>
            Excede el límite de horas regulares (diario y/o semanal)
          </div>
          {submitError.violations.map((v, i) => (
            <div key={i} style={{ fontSize: 12, color: '#991b1b', marginBottom: 3 }}>
              <strong>{v.employee_name}</strong>: {v.message}
            </div>
          ))}
        </div>
      )}

      {/* AMGtime refresh bar — engineers, managers, reviewers, admins only */}
      {sheet && canSeeAmg && (
        <div style={{
          ...bubbleStyle, padding: '8px 14px', marginBottom: 10,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: '#f8fafc',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Clock size={14} style={{ color: NAVY }} />
            <span style={{ fontSize: 12, color: NAVY, fontWeight: 600 }}>AMGtime</span>
            {sheet.amgtime_dept_code && (
              <span style={{ fontSize: 10, background: '#dbeafe', color: '#1e40af', padding: '1px 6px', borderRadius: 10, fontWeight: 600 }}>
                {sheet.amgtime_dept_code}
              </span>
            )}
            {amgLoading && <span style={{ fontSize: 11, color: '#94a3b8' }}>Consultando...</span>}
            {amgError && <span style={{ fontSize: 11, color: '#ef4444' }}>{amgError}</span>}
          </div>
          <button
            onClick={() => refreshAmgValidation()}
            disabled={amgLoading}
            style={{
              fontSize: 10, padding: '2px 8px', borderRadius: 10,
              border: `1px solid ${BORDER_COLOR}`, background: '#fff',
              cursor: amgLoading ? 'not-allowed' : 'pointer', color: NAVY,
              fontFamily: 'Inter, sans-serif',
            }}
          >
            Refrescar
          </button>
        </div>
      )}

      {/* V63 · Resumen de ausencias — la superficie PRINCIPAL.
          Va acá arriba, antes de las tarjetas, porque el flujo real es CLONAR la cuadrilla
          del día anterior y después podar quién no vino: en ese momento la lista ya está
          poblada y el aviso llega ANTES de que se carguen horas. Un aviso solo al agregar
          una persona nunca aparecería en ese flujo, que es el mayoritario. */}
      {cardsConAusencia.length > 0 && (
        <div style={{
          ...bubbleStyle, padding: '12px 16px', marginBottom: 14,
          borderLeft: '3px solid #f59e0b',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
            <AlertTriangle size={15} color="#d97706" />
            <span style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>
              {cardsConAusencia.length === 1
                ? '1 persona con ausencia registrada para esta fecha'
                : `${cardsConAusencia.length} personas con ausencia registrada para esta fecha`}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {cardsConAusencia.map(({ card, ausencia }) => {
              const d = describeAbsence(ausencia);
              return (
                // ⚠ NO se muestra el proyecto de la solicitud. Es información de más y
                // además engañosa: la ausencia puede estar registrada en un proyecto y la
                // persona estar prestada en otro, así que ese código no dice dónde debería
                // estar hoy. Nombre, tipo, estado y horas alcanzan para decidir.
                <div key={card.empId} style={{ fontSize: 12, color: '#475569' }}>
                  <strong style={{ color: NAVY }}>{card.empName}</strong>
                  <span style={{ color: d.color, fontWeight: 600 }}> · {d.texto}</span>
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8, lineHeight: 1.45 }}>
            Es un aviso, no un bloqueo: si la persona igual trabajó, cargá sus horas normalmente.
          </div>
        </div>
      )}

      {/* Employee cards */}
      {cards.map(card => {
        const capStatus = getCapStatus(card);
        const maxRegular = getMaxRegular(card);
        const totalRegular = card.entries.reduce((s, e) => s + (parseFloat(e.regular) || 0), 0);
        const totalOvertime = card.entries.reduce((s, e) => s + (parseFloat(e.overtime) || 0), 0);

        return (
          <div key={card.empId} style={{
            ...bubbleStyle, padding: 0, marginBottom: 12,
            borderLeft: capStatus === 'exceeded' ? '3px solid #ef4444' :
                        capStatus === 'reached' ? '3px solid #16a34a' :
                        capStatus === 'partial' ? '3px solid #f59e0b' : `3px solid ${BORDER_COLOR}`,
          }}>
            {/* Card header */}
            <div style={{
              padding: '10px 14px', display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', borderBottom: `1px solid ${BORDER_COLOR}`,
              background: '#f8fafc', borderRadius: '16px 16px 0 0', flexWrap: 'wrap', gap: 6,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: NAVY }}>{card.empName}</span>
                {/* Work location toggle */}
                {isEditable ? (
                  <button
                    onClick={() => setCards(cards.map(c => c.empId === card.empId
                      ? { ...c, workLocation: c.workLocation === 'remote' ? 'in-person' : 'remote' }
                      : c
                    ))}
                    style={{
                      fontSize: 10, fontWeight: 600, padding: '1px 8px', borderRadius: 10,
                      border: `1px solid ${card.workLocation === 'remote' ? '#7c3aed40' : '#16a34a40'}`,
                      background: card.workLocation === 'remote' ? '#f3e8ff' : '#f0fdf4',
                      color: card.workLocation === 'remote' ? '#7c3aed' : '#16a34a',
                      cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    {card.workLocation === 'remote' ? '🏠 Remoto' : '🏗️ Presencial'}
                  </button>
                ) : (
                  <span style={{
                    fontSize: 10, fontWeight: 600, padding: '1px 8px', borderRadius: 10,
                    border: `1px solid ${card.workLocation === 'remote' ? '#7c3aed40' : '#16a34a40'}`,
                    background: card.workLocation === 'remote' ? '#f3e8ff' : '#f0fdf4',
                    color: card.workLocation === 'remote' ? '#7c3aed' : '#16a34a',
                  }}>
                    {card.workLocation === 'remote' ? '🏠 Remoto' : '🏗️ Presencial'}
                  </span>
                )}
                {/* Compact cap bubbles */}
                {card.alerts && (() => {
                  const otherReg = card.alerts.total_regular || 0;
                  const dailyUsed = otherReg + totalRegular;
                  const weeklyUsed = (card.alerts.weekly_regular || 0) + totalRegular;
                  const dailyColor = dailyUsed > 8 ? '#ef4444' : dailyUsed >= 7 ? '#f59e0b' : '#16a34a';
                  const weeklyColor = weeklyUsed > 40 ? '#ef4444' : weeklyUsed >= 35 ? '#f59e0b' : '#16a34a';
                  return (
                    <>
                      <span title={`Día: ${dailyUsed.toFixed(1)}h de 8h regulares`} style={{
                        fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10,
                        background: dailyColor + '18', color: dailyColor, border: `1px solid ${dailyColor}40`,
                      }}>D: {dailyUsed.toFixed(1)}/8h</span>
                      <span title={`Semana: ${weeklyUsed.toFixed(1)}h de 40h regulares`} style={{
                        fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10,
                        background: weeklyColor + '18', color: weeklyColor, border: `1px solid ${weeklyColor}40`,
                      }}>S: {weeklyUsed.toFixed(1)}/40h</span>
                      <OtrosProyectosChips alerts={card.alerts} />
                    </>
                  );
                })()}
                {/* V63 · Marca de ausencia en la fila — REFUERZO del resumen de arriba.
                    El resumen dice cuántos y quiénes; esto dice CUÁL es cuál mientras se
                    cargan sus horas, y es donde vive la acción. */}
                {/* ⚠⚠ EL CHIP EXISTE SOLO CUANDO HAY ALGO QUE APRETAR. Si la ausencia está
                    pendiente, o el proyecto no tiene el código mapeado, o la tarea YA se
                    cargó, la persona sigue apareciendo en la alerta de arriba y acá no va
                    nada: un chip sin acción es ruido en una fila que ya tiene 4 badges.
                    ⚠ `yaCargada` es ESTADO DERIVADO de las entradas, no un flag que se
                    apaga: si alguien borra la tarea fringe, el chip vuelve solo. */}
                {(() => {
                  const ausencia = absenceFor(card.empId);
                  if (!ausencia) return null;
                  const d = describeAbsence(ausencia);
                  const target = sheet?.fringe_targets?.[ausencia.type] || null;
                  const yaCargada = tieneTareaFringe(card, target);
                  if (!isEditable || !d.accionable || !target || yaCargada) return null;
                  return (
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10,
                      background: d.bg, color: d.color, border: `1px solid ${d.borde}`,
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                    }} title={`${ausencia.start_date} — ${ausencia.end_date}`}>
                      <Calendar size={10} /> {d.texto}
                      <button
                        type="button"
                        onClick={() => cargarAusencia(card.empId, ausencia, target)}
                        style={{
                          background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8,
                          padding: '1px 7px', fontSize: 9.5, fontWeight: 700, cursor: 'pointer',
                          fontFamily: 'Inter, sans-serif',
                        }}
                        title={`Carga ${target.code} — ${target.label} con ${formatHours(ausencia.hours_per_day || 0)} h`}
                      >
                        Cargar {target.code}
                      </button>
                    </span>
                  );
                })()}

                {/* AMGtime inline alert */}
                {canSeeAmg && (() => {
                  const amg = getAmgForEmployee(card.empName);
                  if (!amg) return null;
                  if (amg.status === 'no_punch') {
                    return (
                      <span title="Sin registro de ponche en AMGtime" style={{
                        fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10,
                        background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a',
                        display: 'inline-flex', alignItems: 'center', gap: 3,
                      }}><AlertTriangle size={10} /> Sin ponche</span>
                    );
                  }
                  // Live comparison against AMGtime punch data
                  const cardReg = card.entries.reduce((s, e) => s + (parseFloat(e.regular) || 0), 0);
                  const cardOt = card.entries.reduce((s, e) => s + (parseFloat(e.overtime) || 0), 0);
                  const punchReg = amg.punched?.reg || 0;
                  const punchOt = amg.punched?.ot || 0;
                  const LIVE_TOL = 0.15;
                  const isMatch = Math.abs(cardReg - punchReg) < LIVE_TOL && Math.abs(cardOt - punchOt) < LIVE_TOL;
                  if (isMatch) {
                    return (
                      <span title={`AMG: ${formatHours(punchReg)}h REG${punchOt > 0 ? ` + ${formatHours(punchOt)}h OT` : ''}`} style={{
                        fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10,
                        background: '#f0fdf4', color: '#16a34a', border: '1px solid #16a34a40',
                        display: 'inline-flex', alignItems: 'center', gap: 3,
                      }}><CheckCircle2 size={10} /> AMG: {formatHours(punchReg)}h</span>
                    );
                  }
                  return (
                    <span title={`AMG: ${formatHours(punchReg)}h REG${punchOt > 0 ? ` + ${formatHours(punchOt)}h OT` : ''} / Tarjeta: ${formatHours(cardReg)}h REG${cardOt > 0 ? ` + ${formatHours(cardOt)}h OT` : ''}`} style={{
                      fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10,
                      background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a',
                      display: 'inline-flex', alignItems: 'center', gap: 3,
                    }}><AlertTriangle size={10} /> AMG: {formatHours(punchReg)}h / Tarjeta: {formatHours(cardReg)}h</span>
                  );
                })()}
              </div>
              {isEditable && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <button
                    onClick={() => setCopyingFrom(copyingFrom === card.empId ? null : card.empId)}
                    title="Copiar actividades a otro empleado"
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: copyingFrom === card.empId ? AMBER_DARK : '#64748b', padding: 2,
                    }}
                  >
                    <Copy size={14} />
                  </button>
                  <button
                    onClick={() => { setConvertingCard(convertingCard === card.empId ? null : card.empId); setConvertReason('class'); }}
                    title="Marcar ausencia (clase / personal / falta)"
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: convertingCard === card.empId ? AMBER_DARK : '#64748b', padding: 2,
                    }}
                  >
                    <UserX size={14} />
                  </button>
                  <button onClick={() => removeEmployee(card.empId)} style={{
                    background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 2,
                  }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
            </div>

            {/* Convert-to-absence picker */}
            {convertingCard === card.empId && isEditable && (
              <div style={{
                padding: '8px 14px', background: '#fffbeb', borderBottom: `1px solid ${BORDER_COLOR}`,
                display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
              }}>
                <span style={{ fontSize: 12, color: '#92400e', fontWeight: 600 }}>Motivo de ausencia:</span>
                <select
                  value={convertReason}
                  onChange={e => setConvertReason(e.target.value)}
                  style={{ ...fieldStyle, width: 'auto', fontSize: 13 }}
                >
                  {Object.entries(ABSENCE_REASONS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
                <button
                  onClick={() => convertCardToAbsence(card.empId, convertReason)}
                  style={{ ...navyBtn, padding: '4px 12px', fontSize: 12 }}
                >
                  Convertir a ausencia
                </button>
                <button
                  onClick={() => { setConvertingCard(null); setConvertReason('class'); }}
                  style={{
                    background: 'none', border: `1px solid ${BORDER_COLOR}`, borderRadius: 6,
                    padding: '4px 10px', fontSize: 11, cursor: 'pointer', color: '#64748b',
                  }}
                >
                  Cancelar
                </button>
              </div>
            )}

            {/* Copy activities picker */}
            {copyingFrom === card.empId && (
              <div style={{
                padding: '8px 14px', background: '#fffbeb', borderBottom: `1px solid ${BORDER_COLOR}`,
                display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
              }}>
                <span style={{ fontSize: 12, color: '#92400e', fontWeight: 600 }}>Copiar a:</span>
                <div style={{ flex: 1, minWidth: 140 }}>
                  <SearchableSelect
                    items={employees.filter(e => !cards.find(c => c.empId === e.id)).map(e => ({
                      id: e.id,
                      label: e.name,
                      sublabel: e.email,
                    }))}
                    value={null}
                    onChange={targetId => {
                      copyActivitiesToEmployee(card.empId, targetId);
                      setCopyingFrom(null);
                    }}
                    placeholder="Seleccionar empleado..."
                  />
                </div>
                <button
                  onClick={() => setCopyingFrom(null)}
                  style={{
                    background: 'none', border: `1px solid ${BORDER_COLOR}`, borderRadius: 6,
                    padding: '4px 10px', fontSize: 11, cursor: 'pointer', color: '#64748b',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  Cancelar
                </button>
              </div>
            )}

            {/* Cross-project detail — only when there are hours on other projects */}
            <OtrosProyectosDetalle alerts={card.alerts} />

            {/* Task rows */}
            <div style={{ padding: '8px 14px' }}>
              {/* Header row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, fontSize: 11, fontWeight: 600, color: '#94a3b8' }}>
                <div style={{ flex: 1 }}>{isGlMode ? 'Cuenta GL' : 'Tarea'}</div>
                <div style={{ width: 56, textAlign: 'center' }}>R</div>
                <div style={{ width: 56, textAlign: 'center' }}>E</div>
                {isEditable && <div style={{ width: 24 }} />}
              </div>

              {card.entries.map((entry, taskIdx) => {
                // V67 — ¿esta tarea pide nota de dificultad?
                const codigoTarea = costCodes.find(c => c.id === entry.costCodeId)?.code;
                const pideNota = !isGlMode && TAREAS_CON_NOTA.includes(codigoTarea);
                return (
                <div key={taskIdx} style={{ marginBottom: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {/* Cost code picker (Change 2) */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {isEditable ? (
                      <SearchableSelect
                        items={(isGlMode ? glAccounts : costCodes).map(c => ({
                          id: c.id,
                          label: isGlMode ? formatGlLabel(c.code, c.name) : `${c.code} ${c.description}`,
                        }))}
                        value={isGlMode ? entry.glCodeId : entry.costCodeId}
                        onChange={v => updateTask(card.empId, taskIdx, isGlMode ? 'glCodeId' : 'costCodeId', v)}
                        placeholder={isGlMode ? 'Buscar cuenta GL...' : 'Buscar tarea...'}
                        disabled={!isEditable}
                      />
                    ) : (
                      <span style={{ fontSize: 12, color: NAVY }}>
                        {isGlMode ? (
                          (() => {
                            const gl = glAccounts.find(c => c.id === entry.glCodeId);
                            return gl ? formatGlLabel(gl.code, gl.name) : '—';
                          })()
                        ) : (
                          <>
                            {(costCodes.find(c => c.id === entry.costCodeId)?.code) || '—'}{' '}
                            <span style={{ color: '#94a3b8' }}>
                              ({costCodes.find(c => c.id === entry.costCodeId)?.description || ''})
                            </span>
                          </>
                        )}
                      </span>
                    )}
                  </div>

                  {/* Regular hours (Change 5 — cap enforcement) */}
                  <div style={{ width: 56 }}>
                    {isEditable ? (
                      <input
                        type="number"
                        value={entry.regular || ''}
                        onChange={e => updateTask(card.empId, taskIdx, 'regular', parseFloat(e.target.value) || 0)}
                        min={0}
                        max={maxRegular}
                        step={0.25}
                        placeholder="0"
                        style={{
                          ...fieldStyle, width: '100%', textAlign: 'center',
                          padding: '5px 2px', fontSize: 13,
                        }}
                      />
                    ) : (
                      <span style={{ fontSize: 13, color: NAVY, display: 'block', textAlign: 'center' }}>
                        {entry.regular || '—'}
                      </span>
                    )}
                  </div>

                  {/* Overtime hours */}
                  <div style={{ width: 56 }}>
                    {isEditable ? (
                      <input
                        type="number"
                        value={entry.overtime || ''}
                        onChange={e => updateTask(card.empId, taskIdx, 'overtime', parseFloat(e.target.value) || 0)}
                        min={0} step={0.25}
                        placeholder="0"
                        style={{
                          ...fieldStyle, width: '100%', textAlign: 'center',
                          padding: '5px 2px', fontSize: 13,
                        }}
                      />
                    ) : (
                      <span style={{ fontSize: 13, color: NAVY, display: 'block', textAlign: 'center' }}>
                        {entry.overtime || '—'}
                      </span>
                    )}
                  </div>

                  {/* Remove task */}
                  {isEditable && (
                    <div style={{ width: 24 }}>
                      {card.entries.length > 1 && (
                        <button onClick={() => removeTask(card.empId, taskIdx)} style={{
                          background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 2,
                        }}>
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* ── V67 — Nota de dificultad ──────────────────────────────────
                    SEGUNDA LÍNEA, a ancho completo, y solo cuando la tarea la pide.
                    ⚠ No va en la línea principal a propósito: ahí el único espacio
                    elástico es el del selector de tarea, y comprimirlo empeoraría TODAS
                    las filas para beneficiar a una. Como bloque aparte, el resto de las
                    filas no cambia ni un pixel.
                    ⚠ Con etiqueta y no solo placeholder: el placeholder desaparece al
                    escribir, y quien vuelve a la tarjeta ya no ve qué le pedían. */}
                {pideNota && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginTop: 4, paddingLeft: 14 }}>
                    <span style={{ fontSize: 11, color: '#94a3b8', paddingTop: 6, whiteSpace: 'nowrap' }}>
                      ↳ Dificultad trabajada
                    </span>
                    {isEditable ? (
                      <textarea
                        // Foco automático al aparecer: con 169 tarjetas al año, obligar a
                        // buscar el campo con el mouse cada vez es la diferencia entre que
                        // lo llenen y que escriban "x" para pasar la validación.
                        autoFocus={!entry.difficultyNote}
                        rows={1}
                        value={entry.difficultyNote || ''}
                        onChange={e => updateTask(card.empId, taskIdx, 'difficultyNote', e.target.value)}
                        onInput={e => { e.target.style.height = 'auto'; e.target.style.height = `${e.target.scrollHeight}px`; }}
                        placeholder="Describí la dificultad de esta tarea"
                        style={{
                          ...fieldStyle, flex: 1, fontSize: 12, padding: '5px 8px',
                          resize: 'none', overflow: 'hidden', minHeight: 30, lineHeight: 1.4,
                          borderColor: entry.difficultyNote?.trim() ? undefined : '#fbbf24',
                        }}
                      />
                    ) : (
                      <span style={{ fontSize: 12, color: entry.difficultyNote ? '#334155' : '#b45309' }}>
                        {entry.difficultyNote || '(sin especificar)'}
                      </span>
                    )}
                  </div>
                )}
                </div>
                );
              })}

              {/* Add task button */}
              {isEditable && (
                <button onClick={() => addTask(card.empId)} style={{
                  background: 'none', border: 'none', cursor: 'pointer', color: '#64748b',
                  fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, padding: '4px 0',
                }}>
                  <Plus size={12} /> Agregar tarea
                </button>
              )}

              {/* Per-employee notes */}
              {isEditable ? (
                <textarea
                  value={card.empNotes || ''}
                  onChange={e => updateEmpNotes(card.empId, e.target.value)}
                  rows={1}
                  placeholder="Notas para este empleado..."
                  style={{ ...fieldStyle, resize: 'vertical', marginTop: 6, fontSize: 12 }}
                />
              ) : (
                card.empNotes && (
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 6, fontStyle: 'italic' }}>
                    {card.empNotes}
                  </div>
                )
              )}

              {/* Card totals */}
              <div style={{
                display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 6,
                paddingTop: 6, borderTop: `1px solid ${BORDER_COLOR}`,
                fontSize: 12, fontWeight: 600,
              }}>
                <span style={{ color: NAVY }}>{formatHours(totalRegular)}</span>
                <span style={{ color: NAVY }}>{formatHours(totalOvertime)}</span>
              </div>

              {/* Inline warning when regular hours exceed 8 */}
              {(() => {
                const otherProjectRegular = card.alerts?.total_regular || 0;
                const dailyTotal = otherProjectRegular + totalRegular;
                const excess = dailyTotal - 8;
                if (excess > 0) return (
                  <div style={{
                    marginTop: 6, padding: '6px 10px', borderRadius: 6,
                    background: '#fef2f2', border: '1px solid #fecaca',
                    fontSize: 12, color: '#991b1b', display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    <AlertTriangle size={14} style={{ flexShrink: 0 }} />
                    Excede 8h regulares por {excess.toFixed(2)}h. Mueva el exceso a Horas Extra (E) antes de someter.
                  </div>
                );
                return null;
              })()}
            </div>
          </div>
        );
      })}

      {/* Add employee (Change 2 — searchable) */}
      {isEditable && (
        <div style={{ marginBottom: 14, maxWidth: 300 }}>
          <SearchableSelect
            items={availableEmployees.map(e => ({
              id: e.id,
              label: e.name,
              sublabel: e.email,
            }))}
            value={null}
            onChange={empId => addEmployee(empId)}
            placeholder="Buscar empleado..."
          />
        </div>
      )}

      {/* Unpaid Absences (Ausencias no remuneradas) — always rendered so users
          can see attendance state in any sheet status (read-only when not editable). */}
      {(
        <div style={{ ...bubbleStyle, padding: '12px 16px', marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: NAVY, letterSpacing: 0.5 }}>
              AUSENCIAS NO REMUNERADAS
            </div>
            {isEditable && !addingAbsence && (
              <button
                onClick={() => { setAddingAbsence(true); setPendingAbs({ employee_id: null, reason: 'class', notes: '' }); }}
                style={{
                  background: 'none', border: `1px solid ${BORDER_COLOR}`, borderRadius: 6,
                  padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: NAVY,
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}
              >
                <Plus size={12} /> Marcar Ausencia
              </button>
            )}
          </div>

          {absences.length === 0 && !addingAbsence && (
            <div style={{ fontSize: 12, color: '#94a3b8' }}>Sin ausencias para esta tarjeta.</div>
          )}

          {absences.map(a => {
            const isEditing = editingAbsenceId === a.id;
            return (
              <div key={a.id} style={{
                display: 'grid', gridTemplateColumns: '1fr 120px 1fr auto',
                gap: 8, alignItems: 'center',
                padding: '6px 0', borderBottom: `1px solid ${BORDER_COLOR}`,
                fontSize: 13,
              }}>
                <span style={{ color: NAVY, fontWeight: 500 }}>{a.employee_name || `Empleado #${a.employee_id}`}</span>
                {isEditing ? (
                  <select
                    value={editingAbsData.reason}
                    onChange={e => setEditingAbsData(d => ({ ...d, reason: e.target.value }))}
                    style={{ ...fieldStyle, fontSize: 13 }}
                  >
                    {Object.entries(ABSENCE_REASONS).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                ) : (
                  <span style={{ color: '#64748b' }}>{ABSENCE_REASONS[a.reason] || a.reason}</span>
                )}
                {isEditing ? (
                  <input
                    value={editingAbsData.notes}
                    onChange={e => setEditingAbsData(d => ({ ...d, notes: e.target.value }))}
                    placeholder="Notas"
                    style={{ ...fieldStyle, fontSize: 13 }}
                  />
                ) : (
                  <span style={{ color: a.notes ? '#64748b' : '#cbd5e1', fontStyle: a.notes ? 'normal' : 'italic' }}>
                    {a.notes || '—'}
                  </span>
                )}
                {isEditable && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    {isEditing ? (
                      <>
                        <button
                          onClick={saveAbsenceEdit}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#16a34a', padding: 2 }}
                          title="Guardar"
                        >
                          <Check size={14} />
                        </button>
                        <button
                          onClick={() => setEditingAbsenceId(null)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 2 }}
                          title="Cancelar"
                        >
                          <X size={14} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => startEditAbsence(a)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 2 }}
                          title="Editar"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          onClick={() => removeAbsence(a.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 2 }}
                          title="Eliminar"
                        >
                          <Trash2 size={13} />
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {addingAbsence && isEditable && (
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 120px 1fr auto auto',
              gap: 8, alignItems: 'center', marginTop: 10,
            }}>
              <SearchableSelect
                items={employees
                  .filter(e =>
                    !absences.find(a => a.employee_id === e.id) &&
                    !cards.find(c => c.empId === e.id)
                  )
                  .map(e => ({ id: e.id, label: e.name, sublabel: e.email }))}
                value={pendingAbs.employee_id}
                onChange={empId => setPendingAbs(p => ({ ...p, employee_id: empId }))}
                placeholder="Empleado..."
              />
              <select
                value={pendingAbs.reason}
                onChange={e => setPendingAbs(p => ({ ...p, reason: e.target.value }))}
                style={{ ...fieldStyle, fontSize: 13 }}
              >
                {Object.entries(ABSENCE_REASONS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
              <input
                value={pendingAbs.notes}
                onChange={e => setPendingAbs(p => ({ ...p, notes: e.target.value }))}
                placeholder="Notas (opcional)"
                style={{ ...fieldStyle, fontSize: 13 }}
              />
              <button
                onClick={saveNewAbsence}
                disabled={!pendingAbs.employee_id}
                style={{ ...navyBtn, padding: '4px 12px', fontSize: 12, opacity: pendingAbs.employee_id ? 1 : 0.5 }}
              >
                Guardar
              </button>
              <button
                onClick={() => { setAddingAbsence(false); setPendingAbs({ employee_id: null, reason: 'class', notes: '' }); }}
                style={{
                  background: 'none', border: `1px solid ${BORDER_COLOR}`, borderRadius: 6,
                  padding: '4px 10px', fontSize: 11, cursor: 'pointer', color: '#64748b',
                }}
              >
                Cancelar
              </button>
            </div>
          )}
        </div>
      )}

      {/* Notes */}
      <div style={{ ...bubbleStyle, padding: '12px 16px', marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: NAVY, marginBottom: 6 }}>Notas generales de la tarjeta</div>
        {isEditable ? (
          <textarea
            value={sheetNotes}
            onChange={e => setSheetNotes(e.target.value)}
            rows={2}
            placeholder="Notas del supervisor..."
            style={{ ...fieldStyle, resize: 'vertical' }}
          />
        ) : (
          <div style={{ fontSize: 13, color: sheetNotes ? NAVY : '#94a3b8' }}>
            {sheetNotes || 'Sin notas.'}
          </div>
        )}
      </div>

      {/* Actions */}
      {isEditable && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'center' }}>
          {saveMsg && (
            <span style={{ fontSize: 13, fontWeight: 600, color: '#16a34a', marginRight: 4 }}>{saveMsg}</span>
          )}
          {/* V30.2: delete button (drafts only — backend deleteSheet rejects status != 'draft') */}
          {sheet.status === 'draft' && (
            <button
              onClick={async () => {
                if (!window.confirm('¿Eliminar este borrador? Esta acción no se puede deshacer.')) return;
                try {
                  await client.delete(`/api/nomina/sheets/${sheet.id}`);
                  navigate(`/horas/proyecto/${sheet.project_id}`);
                } catch (err) {
                  alert(err.response?.data?.error || 'Error al eliminar la tarjeta.');
                }
              }}
              disabled={saving || submitting}
              style={{
                background: 'none', color: '#dc2626', border: '1px solid #fecaca',
                borderRadius: 8, padding: '8px 14px', fontWeight: 600,
                fontSize: 13, cursor: (saving || submitting) ? 'not-allowed' : 'pointer',
                fontFamily: 'Inter, sans-serif',
                display: 'flex', alignItems: 'center', gap: 6,
                opacity: (saving || submitting) ? 0.6 : 1,
              }}
            >
              <Trash2 size={14} /> Eliminar Borrador
            </button>
          )}
          <button onClick={handleSave} disabled={saving} style={{
            ...navyBtn, display: 'flex', alignItems: 'center', gap: 6,
            background: '#64748b', padding: '8px 16px', fontSize: 13,
            opacity: saving ? 0.6 : 1,
          }}>
            <Save size={14} /> {saving ? 'Guardando...' : 'Guardar'}
          </button>
          <button onClick={handleSubmit} disabled={submitting} style={{
            ...navyBtn, display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 16px', fontSize: 13,
            opacity: submitting ? 0.6 : 1,
          }}>
            <Send size={14} /> {submitting ? (sheet.status === 'returned' ? 'Reenviando...' : 'Sometiendo...') : (sheet.status === 'returned' ? 'Reenviar' : 'Someter')}
          </button>
        </div>
      )}

      {/* Exit modal — save / delete / continue */}
      {showExitModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', border: `1.5px solid ${NAVY}`, borderRadius: 16,
            boxShadow: '0 4px 24px rgba(27,58,107,0.18)', padding: '32px 36px',
            maxWidth: 420, width: '90%',
          }}>
            <div style={{ fontSize: 20, fontWeight: 'bold', color: NAVY, marginBottom: 8 }}>
              Cambios sin guardar
            </div>
            <div style={{ fontSize: 14, color: '#555', marginBottom: 24 }}>
              ¿Qué deseas hacer con esta tarjeta?
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                onClick={async () => {
                  await handleSave();
                  navigate(`/horas/proyecto/${sheet.project_id}`);
                }}
                style={{
                  background: NAVY, color: '#fff', border: 'none', borderRadius: 8,
                  padding: '11px 0', fontWeight: 'bold', fontSize: 14, cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                💾 Guardar como Borrador
              </button>
              <button
                onClick={async () => {
                  try {
                    await client.delete(`/api/nomina/sheets/${sheet.id}`);
                  } catch {}
                  navigate(`/horas/proyecto/${sheet.project_id}`);
                }}
                style={{
                  background: '#fff', color: '#c0392b', border: '1.5px solid #c0392b',
                  borderRadius: 8, padding: '11px 0', fontWeight: 'bold', fontSize: 14,
                  cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                }}
              >
                🗑 Descartar Tarjeta
              </button>
              <button
                onClick={() => setShowExitModal(false)}
                style={{
                  background: '#fff', color: NAVY, border: `1.5px solid ${NAVY}`,
                  borderRadius: 8, padding: '11px 0', fontWeight: 'bold', fontSize: 14,
                  cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                }}
              >
                ← Seguir Editando
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
